import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { myEnrollments, certificateUrl, v2AdminListCourses } from "../lib/api";
import { ArrowRight, Award, BookOpen, LogOut, Sparkles } from "lucide-react";

const StudentDashboard = () => {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [v2Courses, setV2Courses] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth/login?redirect=/academy/dashboard"); return; }
    myEnrollments().then(setEnrollments);
    // V2-Kurse anzeigen, wenn Entitlement vorhanden (nur für Admins listbar,
    // Learner sehen sie über die Enrollment-Verknüpfung — dieser Bereich zeigt
    // sie nur, wenn der User ein Entitlement für den entsprechenden course_id hat).
    v2AdminListCourses().then((cs) => setV2Courses(cs || [])).catch(() => setV2Courses([]));
  }, [user, loading, nav]);

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-28 min-h-screen" data-testid="student-dashboard">
      <div className="container-sat max-w-[1100px] py-10">
        <div className="flex justify-between items-end mb-10">
          <div>
            <div className="overline mb-2">Mein Bereich</div>
            <h1 className="headline-xl text-4xl md:text-5xl">Willkommen, {user.name}</h1>
          </div>
          <button onClick={() => { logout(); nav("/"); }} className="btn-secondary text-sm"><LogOut size={14} /> Abmelden</button>
        </div>

        <div className="glass rounded-2xl p-6 md:p-10">
          <div className="flex items-center justify-between mb-6">
            <div className="serif text-2xl">Meine Kurse</div>
            <Link to="/academy" className="btn-ghost text-sm">Alle Kurse →</Link>
          </div>

          {enrollments.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen size={40} className="text-white/30 mx-auto mb-4" />
              <p className="text-white/65 mb-6">Du bist noch in keinem Kurs eingeschrieben.</p>
              <Link to="/academy" className="btn-primary">Kurse entdecken <ArrowRight size={16} /></Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="my-enrollments">
              {enrollments.map((e) => (
                <div key={e.id} className="border border-white/8 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`enrollment-${e.course_slug}`}>
                  <div className="flex-1">
                    <div className="serif text-xl">{e.course?.title}</div>
                    <div className="text-xs text-white/45 mt-1">{(e.completed_lesson_ids?.length || 0)} Lektionen erledigt</div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/academy/${e.course_slug}/learn`} className="btn-primary text-sm">{e.completed ? "Zertifikat ansehen" : "Weiterlernen"} <ArrowRight size={14} /></Link>
                    {e.completed && (
                      <a href={certificateUrl(e.course_slug)} target="_blank" rel="noreferrer" className="btn-secondary text-sm"><Award size={14} /></a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {v2Courses.length > 0 && (
          <div className="glass rounded-2xl p-6 md:p-10 mt-8" data-testid="v2-courses-section">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles size={16} className="text-[#D4AF37]" />
              <div className="serif text-2xl">V2 Kurs-Engine · Interne Kurse</div>
            </div>
            <div className="space-y-3">
              {v2Courses.map((c) => (
                <Link key={c.id} to={`/academy/v2/${c.slug}`}
                  data-testid={`v2-dashboard-course-${c.slug}`}
                  className="border border-white/8 rounded-xl p-5 flex items-center gap-4 hover:bg-white/[0.03] transition">
                  <div className="flex-1">
                    <div className="serif text-xl">{c.title}</div>
                    <div className="text-xs text-white/45 mt-1">{c.slug} · {c.internal_only ? "INTERNAL" : "PUBLIC"} · {c.status}</div>
                  </div>
                  <ArrowRight size={16} className="text-white/50" />
                </Link>
              ))}
            </div>
            <p className="text-white/45 text-xs mt-4">
              V2-Kurse sind derzeit nur intern. Ein Learner ohne INTERNAL_TEST-Entitlement erhält beim Öffnen einen 403.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
